import { useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type FindingGroup = components['schemas']['FindingGroupOut']
type Finding = components['schemas']['FindingOut']
type AuditRun = components['schemas']['AuditRunOut']

/**
 * The technical audit — S-D5's audit screen, on real findings (S-B23).
 *
 * **Organised by who acts, not by severity**, and the design's own note is the
 * argument: *"a list sorted by severity buries the four items the tenant actually
 * has to do"*. So the page is two cards. The first is what needs a human at the
 * organisation, open. The second is what AugRank can fix, collapsed.
 *
 * **The second card says "AugRank can fix these", not "fixed automatically".**
 * S-D5's mock says the latter because it is drawn for the finished product; the
 * pipeline that writes those fixes is S-B25, the gate is S-B26 and the
 * publishers are S-B28, which has not landed. Until it does, claiming a fix
 * happened would be the one kind of wrong an audit screen must never be.
 *
 * Its note points at `/seo/approvals` since S-B27, which is the release that
 * made the destination exist — before it, the same sentence said the queue
 * "arrives in a later release", and leaving that in place after it shipped
 * would have been the same lie in the other direction.
 *
 * **Nothing here knows what a finding code means.** The label, the severity, the
 * remedy and the count all come off the API, because `app/seo/findings.CATALOGUE`
 * is where the actor split is decided — a copy of it in TypeScript would be the
 * hand-written DTO REQUIREMENTS §9.1 forbids, and it would be the copy that
 * disagreed about which column something belongs in.
 */
export function TechnicalAudit(): JSX.Element {
  const toast = useToast()
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  const audit = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/audit', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId],
  )

  const start = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/audit', { params: { path: { site_id: id } } }),
    ),
  )

  const data = audit.state.data
  const running = data?.running ?? false

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Technical audit</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            className="ar-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
        {site !== null ? (
          <Button
            disabled={running || start.pending}
            onClick={() => {
              void start.run(site.id).then((result) => {
                if (!result.ok) return
                audit.refetch()
                toast.show({
                  kind: 'success',
                  title: result.data.queued ? 'Audit queued' : 'An audit is already running',
                  body: result.data.queued
                    ? `AugRank will crawl ${site.domain} and report back in a few minutes.`
                    : `AugRank is already crawling ${site.domain}. This will finish shortly.`,
                })
              })
            }}
          >
            {running ? 'Audit running…' : 'Run audit now'}
          </Button>
        ) : null}
      </div>

      {start.state.status === 'error' ? <InlineError failure={start.state.failure} /> : null}

      {sites.state.status === 'loading' ? (
        <Loading label="Loading your sites" />
      ) : sites.state.status === 'error' ? (
        <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
      ) : site === null ? (
        <Empty
          title="No site yet"
          body="Add your website and AugRank can check it for the things that stop Google indexing it."
          action={<Link to="/onboarding/site">Add your site</Link>}
        />
      ) : audit.state.status === 'loading' ? (
        <Loading label="Loading the audit" />
      ) : audit.state.status === 'error' ? (
        <ErrorState failure={audit.state.failure} onRetry={audit.refetch} />
      ) : data === null || data.run === null || data.run === undefined ? (
        <Empty
          title="No audit yet"
          body={`AugRank has not checked ${site.domain}. Running one takes a couple of minutes and makes about two hundred requests to your server.`}
        />
      ) : (
        <div className="ar-stack">
          <Summary run={data.run} score={data.health_score ?? null} findings={data} />
          <Column
            heading="Needs a decision from you"
            tone="warning"
            groups={data.needs_you}
            empty="Nothing on this site needs you right now."
          />
          <Column
            heading="AugRank can fix these"
            tone="success"
            groups={data.we_can_fix}
            empty="Nothing here for AugRank to fix."
            note={
              <>
                These are changes AugRank is able to make on its own — adding a tag, describing an
                image, publishing a sitemap. Nothing has been changed yet, and nothing will be
                without your say-so: they are proposed together as one item in the{' '}
                <Link to="/seo/approvals">approval queue</Link>. The list on that card is capped, so
                a crawl that found hundreds shows the first of them and says it was shortened — this
                column is the whole set.
              </>
            }
          />
          <RecentlyFixed findings={data.recently_fixed} />
        </div>
      )}
    </>
  )
}

/**
 * The four numbers across the top, and the honest sentence underneath.
 *
 * A `partial` run is the one that needs saying out loud: the audit produced
 * findings and something it wanted — almost always PageSpeed Insights — did not
 * answer. A screen that rendered that identically to a clean run would be
 * reporting an absent measurement as a passing one.
 */
function Summary({
  run,
  score,
  findings,
}: {
  run: AuditRun
  score: number | null
  findings: { needs_you: FindingGroup[]; we_can_fix: FindingGroup[] }
}): JSX.Element {
  const summary = (run.summary ?? {}) as Record<string, unknown>
  const pages = typeof summary.pages_crawled === 'number' ? summary.pages_crawled : 0
  const brokenLinks = typeof summary.broken_links === 'number' ? summary.broken_links : 0
  const needsYou = findings.needs_you.reduce((total, group) => total + group.count, 0)
  const canFix = findings.we_can_fix.reduce((total, group) => total + group.count, 0)
  const bounded = (summary.bounded ?? {}) as Record<string, unknown>

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>
          {pages.toLocaleString('en-IN')} page{pages === 1 ? '' : 's'} checked
        </h3>
        <span className="ar-card__spacer" />
        {run.status === 'queued' || run.status === 'running' ? (
          <span className="ar-badge">Running</span>
        ) : run.status === 'partial' ? (
          <span className="ar-badge ar-badge--warning">Partly measured</span>
        ) : run.status === 'failed' ? (
          <span className="ar-badge ar-badge--danger">Failed</span>
        ) : run.finished_at !== null && run.finished_at !== undefined ? (
          <span className="ar-text-body-sm ar-muted">{formatDate(run.finished_at)}</span>
        ) : null}
      </div>
      <div className="ar-card__body">
        <div className="app-stats">
          <Stat label="Health score" value={score === null ? '—' : String(score)} />
          <Stat label="Needs you" value={String(needsYou)} />
          <Stat label="AugRank can fix" value={String(canFix)} />
          <Stat label="Broken links" value={String(brokenLinks)} />
        </div>
        <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
          The health score is a summary of what is open, not a measurement — it moves when something
          is fixed and it is not comparable with another site&rsquo;s.
        </p>
        {run.status === 'partial' ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {describeDegradation(summary)}
          </p>
        ) : null}
        {bounded.page_limit_reached === true ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            The site has more pages than one audit covers, so this is a sample rather than a
            complete sweep. The pages your sitemap lists were checked first.
          </p>
        ) : null}
      </div>
    </section>
  )
}

/** What a `partial` run could not measure, in the tenant's terms. */
function describeDegradation(summary: Record<string, unknown>): string {
  const unavailable = (summary.pagespeed_unavailable ?? {}) as Record<string, unknown>
  const strategies = Object.keys(unavailable)
  if (strategies.length > 0) {
    return (
      `Google\u2019s PageSpeed service did not answer for ${strategies.join(' and ')}, so the ` +
      'speed measurements are missing from this run. Everything else was checked.'
    )
  }
  const bounded = (summary.bounded ?? {}) as Record<string, unknown>
  if (bounded.out_of_time === true) {
    return 'The audit ran out of its time budget before it finished, so this covers part of the site. Everything it did check is below.'
  }
  return 'Part of this audit could not be completed. Everything that was checked is below.'
}

/**
 * One of the screen's two columns: findings grouped by kind, worst first.
 *
 * Each group is a `<details>` so the second column collapses without the page
 * needing state — S-D5 draws the agent column collapsed and the tenant column
 * open, and `open` on the first group of the tenant column is what does that
 * without a click.
 */
function Column({
  heading,
  tone,
  groups,
  empty,
  note,
}: {
  heading: string
  tone: 'warning' | 'success'
  groups: FindingGroup[]
  empty: string
  /** A `ReactNode` since S-B27, so the note can link to the approval queue. */
  note?: ReactNode
}): JSX.Element {
  const total = groups.reduce((sum, group) => sum + group.count, 0)
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>{heading}</h3>
        <span className="ar-card__spacer" />
        <span className={`ar-badge ar-badge--${tone}`}>{total}</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {note !== undefined ? (
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {note}
          </p>
        ) : null}
        {groups.length === 0 ? (
          <p className="ar-empty ar-empty--inline">{empty}</p>
        ) : (
          groups.map((group, index) => (
            <details key={group.code} open={tone === 'warning' && index === 0}>
              <summary>
                <strong>{group.label}</strong>{' '}
                <span className="ar-muted">
                  · {group.count} {group.count === 1 ? 'item' : 'items'} ·{' '}
                  {severityLabel(group.severity)}
                </span>
              </summary>
              <div className="ar-stack ar-stack--tight" style={{ marginTop: 'var(--ar-space-3)' }}>
                <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
                  {group.findings[0]?.remedy}
                </p>
                <ul className="ar-stack ar-stack--tight" style={{ margin: 0 }}>
                  {group.findings.map((finding) => (
                    <FindingRow key={finding.id} finding={finding} />
                  ))}
                </ul>
              </div>
            </details>
          ))
        )}
      </div>
    </section>
  )
}

/**
 * One finding: where it is, and the evidence that makes it checkable by hand.
 *
 * The evidence is the part that matters. *"Broken internal link"* is an
 * assertion; *"/gallery/2019-summer returned 404, linked from 3 pages"* is
 * something a tenant can open in a browser and confirm in ten seconds, which is
 * the difference between an audit that gets acted on and one that gets ignored.
 */
function FindingRow({ finding }: { finding: Finding }): JSX.Element {
  return (
    <li className="ar-text-body-sm">
      {finding.url !== null && finding.url !== undefined ? (
        <a className="ar-link ar-mono" href={finding.url} target="_blank" rel="noreferrer noopener">
          {pathOf(finding.url)}
        </a>
      ) : (
        <span>{finding.title}</span>
      )}
      <span className="ar-muted"> {evidenceOf(finding)}</span>
    </li>
  )
}

/**
 * The one line of evidence a finding carries, per shape of `detail`.
 *
 * Reading a handful of well-known keys rather than dumping the object: `detail`
 * is JSONB whose shape differs per code, and a rendered blob is something a
 * developer reads and a business owner does not. Anything unrecognised falls
 * through to nothing, which is why every branch is a lookup and none is a cast.
 */
function evidenceOf(finding: Finding): string {
  const detail = (finding.detail ?? {}) as Record<string, unknown>
  const parts: string[] = []

  const status = numberAt(detail, 'status')
  if (status !== null && status > 0) parts.push(`HTTP ${status.toFixed(0)}`)

  const referrers = numberAt(detail, 'linked_from_count')
  if (referrers !== null) {
    parts.push(`linked from ${referrers.toFixed(0)} page${referrers === 1 ? '' : 's'}`)
  }

  const withoutAlt = numberAt(detail, 'without_alt')
  const images = numberAt(detail, 'images')
  if (withoutAlt !== null) {
    parts.push(`${withoutAlt.toFixed(0)} of ${images === null ? '?' : images.toFixed(0)} images`)
  }

  const value = numberAt(detail, 'value')
  const metric = stringAt(detail, 'metric')
  if (value !== null && metric !== null) {
    const unit = metric === 'cls' ? '' : ' ms'
    const source = stringAt(detail, 'source') === 'field' ? 'real users' : 'lab test'
    const where = stringAt(detail, 'strategy') ?? 'this site'
    parts.push(`${value.toString()}${unit} on ${where} (${source})`)
  }

  const rule = stringAt(detail, 'rule')
  if (rule !== null && rule !== '') parts.push(`rule \u201C${rule}\u201D`)

  const hops = numberAt(detail, 'hops')
  if (hops !== null) parts.push(`${hops.toFixed(0)} redirects`)

  const reason = stringAt(detail, 'reason')
  if (reason !== null && reason !== '') parts.push(reason)

  return parts.length > 0 ? `\u2014 ${parts.join(', ')}` : ''
}

/**
 * One key of a JSONB blob, if it is the type this screen expects.
 *
 * `detail`'s shape differs per finding code, so every read is a check rather
 * than a cast — a `number` key that arrives as a string from a future revision
 * renders as nothing rather than as `[object Object]`.
 */
function numberAt(detail: Record<string, unknown>, key: string): number | null {
  const value = detail[key]
  return typeof value === 'number' ? value : null
}

function stringAt(detail: Record<string, unknown>, key: string): string | null {
  const value = detail[key]
  return typeof value === 'string' ? value : null
}

/**
 * What the last run found to be gone. The half of a trend a tenant reads first.
 *
 * Absent rather than empty when there is nothing: a card headed *"Fixed since the
 * last run"* with nothing under it reads as a reproach on a first run.
 */
function RecentlyFixed({ findings }: { findings: Finding[] }): JSX.Element | null {
  if (findings.length === 0) return null
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>Fixed since the last audit</h3>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--success">{findings.length}</span>
      </div>
      <div className="ar-card__body">
        <ul className="ar-stack ar-stack--tight" style={{ margin: 0 }}>
          {findings.map((finding) => (
            <li key={finding.id} className="ar-text-body-sm">
              {finding.title}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function severityLabel(severity: string): string {
  if (severity === 'critical') return 'Critical'
  if (severity === 'high') return 'Important'
  if (severity === 'medium') return 'Worth fixing'
  return 'Minor'
}

/** A URL as its path, because every row on the screen is about the same site. */
function pathOf(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="app-stat">
      <span className="app-stat__label">{label}</span>
      <span className="app-stat__value">{value}</span>
    </div>
  )
}
